// BasicInfoStep component matching the HTML design exactly
import React, { useCallback, useState } from "react";
import {
  FormField,
  TextInput,
  ProductImageUpload,
  RichTextEditor,
} from "src/shared/forms/components";
import type { ProductImageData } from "src/shared/forms/components/ProductImageUpload/ProductImageUpload";
import VideoUpload from "src/shared/forms/components/VideoUpload/VideoUpload";
import type { VideoSource } from "../../types/FileUpload";
import fileUploadService from "../../services/fileUploadService";

export interface BasicInfoData {
  productSku?: string;
  productName?: string;
  productDescription?: string;
  productImages?: ProductImageData[];
  videoUrl?: string; // Legacy field - kept for backward compatibility
  productVideo?: VideoSource; // New video field (file upload or URL)
}

export interface BasicInfoStepProps {
  data: BasicInfoData;
  onChange: (data: BasicInfoData) => void;
  errors?: string[];
  isActive?: boolean;
  onValidate?: () => string[];
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  data: basicInfoData,
  onChange,
  errors = [],
  isActive = true,
  onValidate,
}) => {
  // Handle field changes
  const handleFieldChange = useCallback(
    (fieldName: keyof BasicInfoData, value: any) => {
      onChange({
        ...basicInfoData,
        [fieldName]: value,
      });
    },
    [basicInfoData, onChange]
  );

  // Handle file upload to server (for images)
  const handleFileUpload = useCallback(async (file: File): Promise<{ file_id: string; file_url: string }> => {
    try {
      const result = await fileUploadService.uploadFile(file, 'image');
      return {
        file_id: result.file_id,
        file_url: result.file_url
      };
    } catch (error: any) {
      console.error('File upload failed:', error);
      throw new Error(error?.message || 'Failed to upload file to server');
    }
  }, []);

  // Handle video file upload to server
  const handleVideoFileUpload = useCallback(async (file: File): Promise<{ file_id: string; file_url: string }> => {
    try {
      const result = await fileUploadService.uploadFile(file, 'video');
      return {
        file_id: result.file_id,
        file_url: result.file_url
      };
    } catch (error: any) {
      console.error('Video upload failed:', error);
      throw new Error(error?.message || 'Failed to upload video to server');
    }
  }, []);

  // Get error for specific field - improved matching
  const getFieldError = (fieldName: string): string | undefined => {
    const fieldMap: { [key: string]: string[] } = {
      'productSku': ['Product SKU', 'SKU'],
      'productName': ['Product name', 'name'],
      'productDescription': ['Product description', 'description'],
      'videoUrl': ['video URL', 'video'],
      'productVideo': ['video', 'product video'],
      'productImages': ['product image', 'image']
    };
    
    const patterns = fieldMap[fieldName] || [fieldName];
    return errors.find(error => 
      patterns.some(pattern => error.toLowerCase().includes(pattern.toLowerCase()))
    );
  };

  return (
    <div className="tw-space-y-4 md:tw-space-y-6">
      {/* Product Identifier Section - matching HTML design exactly */}
      <div className="tw-bg-white dark:tw-bg-[#1C252E] tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm">
        <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
          <div className="tw-flex tw-items-center tw-gap-2">
            <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
              Product Identifier
            </h3>
          </div>
          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
            Provide an identifier for your product to distinguish it from
            others.
          </p>
        </div>

        <div className="tw-grid tw-gap-6">
          {/* Product SKU */}
          <FormField
            label="Product SKU"
            name="productSku"
            required
            error={getFieldError("productSku")}
          >
            <TextInput
              value={basicInfoData.productSku || ""}
              onChange={(value) => handleFieldChange("productSku", value)}
              placeholder="e.g., SKU-001"
              size="md"
            />
          </FormField>
        </div>
      </div>

      {/* Product Details Section - matching HTML design exactly */}
      <div className="tw-bg-white dark:tw-bg-[#1C252E] tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm">
        <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
          <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
            Product Details
          </h3>
          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
            Provide basic information about your product.
          </p>
        </div>

        <div className="tw-grid tw-gap-6">
          {/* Product Name */}
          <FormField
            label="Product Name"
            name="productName"
            required
            error={getFieldError("productName")}
          >
            <TextInput
              value={basicInfoData.productName || ""}
              onChange={(value) => handleFieldChange("productName", value)}
              placeholder="e.g., Classic Chronograph Watch"
              size="lg"
            />
          </FormField>

          {/* Product Description */}
          <FormField
            label="Product Description"
            name="productDescription"
            required
            error={getFieldError("productDescription")}
          >
            <RichTextEditor
              value={basicInfoData.productDescription || ""}
              onChange={(value) =>
                handleFieldChange("productDescription", value)
              }
              placeholder="Describe your product in detail..."
              maxLength={2000}
            />
          </FormField>
        </div>
      </div>

      {/* Product Images Section - matching HTML design exactly */}
      <div className="tw-bg-white dark:tw-bg-[#1C252E] tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm">
        <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
          <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
            Product Images
          </h3>
          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
            Upload high-quality images to showcase your product.
          </p>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-3 tw-pt-2">
          <div className="tw-flex tw-justify-between tw-items-end">
            <label className="tw-text-sm tw-font-bold tw-text-gray-900 dark:tw-text-gray-100">
              Product Gallery Images <span className="tw-text-red-500">*</span>
            </label>
            <span className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
              {basicInfoData.productImages?.length || 0}/10 uploaded
            </span>
          </div>

          <ProductImageUpload
            images={basicInfoData.productImages || []}
            onChange={(images) => handleFieldChange("productImages", images)}
            onFileUpload={handleFileUpload}
            maxImages={10}
            maxFileSize={5}
            error={getFieldError("productImages")}
            showImageCount={false}
          />

          {/* Error Display */}
          {getFieldError("productImages") && (
            <div className="tw-text-sm tw-text-red-600 dark:tw-text-red-400" role="alert">
              {getFieldError("productImages")}
            </div>
          )}

          {/* Product Video - Match HTML design */}
          <div className="tw-flex tw-flex-col tw-gap-2 tw-pt-4 tw-border-t tw-border-gray-200 dark:tw-border-[#364152]">
            <label className="tw-text-sm tw-font-bold tw-text-gray-900 dark:tw-text-gray-100">
              Product Video
            </label>
            <VideoUpload
              value={basicInfoData.productVideo || null}
              onChange={(video) => handleFieldChange("productVideo", video)}
              onFileUpload={handleVideoFileUpload}
              maxFileSize={100}
              error={getFieldError("productVideo")}
            />
            <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
              Upload a video file or add a video URL (YouTube, Vimeo, etc.) to showcase your
              product in action.
            </p>
          </div>
        </div>
      </div>

      {/* Step Errors Display */}
      {errors.length > 0 && (
        <div className="tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-700 tw-rounded-lg tw-p-4">
          <div className="tw-flex tw-items-start tw-gap-3">
            <span className="material-symbols-outlined tw-text-red-600 dark:tw-text-red-400 tw-mt-0.5">
              error
            </span>
            <div>
              <h4 className="tw-font-medium tw-text-red-800 dark:tw-text-red-300 tw-mb-2">
                Please correct the following errors:
              </h4>
              <ul className="tw-text-sm tw-text-red-700 dark:tw-text-red-400 tw-space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasicInfoStep;
